import React, { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  Shield, Eye, FileText, Clock, Users, AlertTriangle,
  Settings, Globe, Database, Download, Trash2, CheckCircle,
  Info, Loader2, Radio
} from 'lucide-react';
import { apiRequest } from '../../api';
import { useTabPermissions } from '../../v3/hooks/useTabPermissions.js';
import { ReadOnlyGuard } from '../../v3/components/common/ReadOnlyGuard.jsx';
import PrivacyModePanel from './PrivacyModePanel.jsx';
import ConsentManagement from './ConsentManagement.jsx';
import DataRetentionPanel from './DataRetentionPanel.jsx';
import DriverDataRequests from './DriverDataRequests.jsx';
import CountryComplianceRulesPanel from './CountryComplianceRulesPanel.jsx';
import PerformanceScoringConfig from './PerformanceScoringConfig.jsx';
import AuditLogViewer from './AuditLogViewer.jsx';
import PrivacyNoticesPanel from './PrivacyNoticesPanel.jsx';

const TelematicsPrivacyPanel = React.lazy(() => import('./TelematicsPrivacyPanel.jsx'));

// Les libellés d'onglets sont résolus au rendu : une constante de module serait
// figée avant tout changement de langue.
const GDPR_TABS = [
  { key: 'overview', labelKey: 'compliance.tabs.overview', icon: Shield },
  { key: 'privacy-mode', labelKey: 'compliance.tabs.privacyMode', icon: Eye },
  { key: 'consent', labelKey: 'compliance.tabs.consent', icon: FileText },
  { key: 'data-requests', labelKey: 'compliance.tabs.dataRequests', icon: Download },
  { key: 'retention', labelKey: 'compliance.tabs.retention', icon: Clock },
  { key: 'scoring', labelKey: 'compliance.tabs.scoring', icon: Settings },
  { key: 'country-rules', labelKey: 'compliance.tabs.countryRules', icon: Globe },
  { key: 'audit', labelKey: 'compliance.tabs.audit', icon: Database },
  { key: 'notices', labelKey: 'compliance.tabs.notices', icon: FileText },
  { key: 'telematics', labelKey: 'compliance.tabs.telematics', icon: Radio },
];

function GDPRComplianceView({ onMessage }) {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState('overview');

  // V3: Tab permission filtering
  const { visibleTabs, canWriteTab, isReadOnly } = useTabPermissions('compliance', GDPR_TABS.map(tab => ({ id: tab.key, ...tab })));

  // Reset activeTab if current tab is hidden by permissions
  useEffect(() => {
    const visibleKeys = visibleTabs.map(tab => tab.id);
    if (visibleKeys.length > 0 && !visibleKeys.includes(activeTab)) {
      setActiveTab(visibleKeys[0]);
    }
  }, [visibleTabs, activeTab]);

  // Fetch overview stats from tenant-isolated endpoint
  const { data: overviewStats, isLoading: statsLoading } = useQuery({
    queryKey: ['gdpr_overview_stats'],
    queryFn: () => apiRequest('/gdpr-compliance/overview-stats'),
    staleTime: 30000 // 30 seconds
  });

  const stats = overviewStats?.data || {};
  const drivers = stats.drivers || { total: 0, with_consent: 0 };
  const checklist = stats.checklist || [];

  // Calculate dynamic alert messages
  const driversWithoutConsent = drivers.total - drivers.with_consent;

  const renderOverview = () => {
    if (statsLoading) {
      return (
        <div className="flex justify-center items-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
          <span className="ml-2 text-gray-600">{t('compliance.overview.loading')}</span>
        </div>
      );
    }

    // Determine status colors based on values
    const complianceStatus = stats.compliance_score >= 80 ? 'good' : stats.compliance_score >= 50 ? 'warning' : 'danger';
    const consentStatus = drivers.with_consent === drivers.total && drivers.total > 0 ? 'good' : 'warning';
    const requestsStatus = stats.pending_requests > 0 ? 'warning' : 'neutral';
    const purgeStatus = stats.records_to_purge > 0 ? 'warning' : 'neutral';

    return (
      <div className="space-y-6">
        {/* Compliance Status Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <ComplianceCard
            icon={Shield}
            title={t('compliance.overview.cards.score.title')}
            value={`${stats.compliance_score || 0}%`}
            status={complianceStatus}
            description={t('compliance.overview.cards.score.description')}
          />
          <ComplianceCard
            icon={FileText}
            title={t('compliance.overview.cards.consents.title')}
            value={`${drivers.with_consent}/${drivers.total}`}
            status={consentStatus}
            description={t('compliance.overview.cards.consents.description')}
          />
          <ComplianceCard
            icon={Download}
            title={t('compliance.overview.cards.requests.title')}
            value={String(stats.pending_requests || 0)}
            status={requestsStatus}
            description={t('compliance.overview.cards.requests.description')}
          />
          <ComplianceCard
            icon={Clock}
            title={t('compliance.overview.cards.purge.title')}
            value={String(stats.records_to_purge || 0)}
            status={purgeStatus}
            description={t('compliance.overview.cards.purge.description')}
          />
        </div>

        {/* Quick Actions */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold mb-4">{t('compliance.overview.quickActions.title')}</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <QuickActionButton
              icon={Eye}
              label={t('compliance.overview.quickActions.privacyMode')}
              onClick={() => setActiveTab('privacy-mode')}
            />
            <QuickActionButton
              icon={Download}
              label={t('compliance.overview.quickActions.exportData')}
              onClick={() => setActiveTab('data-requests')}
            />
            <QuickActionButton
              icon={Trash2}
              label={t('compliance.overview.quickActions.purgeData')}
              onClick={() => setActiveTab('retention')}
            />
            <QuickActionButton
              icon={FileText}
              label={t('compliance.overview.quickActions.manageConsents')}
              onClick={() => setActiveTab('consent')}
            />
          </div>
        </div>

        {/* GDPR Rights Overview */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold mb-4 flex items-center">
            <Users className="w-5 h-5 mr-2 text-blue-600" />
            {t('compliance.overview.rights.title')}
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <RightCard
              t={t}
              title={t('compliance.overview.rights.access.title')}
              article="Art. 15"
              description={t('compliance.overview.rights.access.description')}
              status="implemented"
            />
            <RightCard
              t={t}
              title={t('compliance.overview.rights.rectification.title')}
              article="Art. 16"
              description={t('compliance.overview.rights.rectification.description')}
              status="implemented"
            />
            <RightCard
              t={t}
              title={t('compliance.overview.rights.erasure.title')}
              article="Art. 17"
              description={t('compliance.overview.rights.erasure.description')}
              status="implemented"
            />
            <RightCard
              t={t}
              title={t('compliance.overview.rights.portability.title')}
              article="Art. 20"
              description={t('compliance.overview.rights.portability.description')}
              status="implemented"
            />
            <RightCard
              t={t}
              title={t('compliance.overview.rights.objection.title')}
              article="Art. 21"
              description={t('compliance.overview.rights.objection.description')}
              status="partial"
            />
            <RightCard
              t={t}
              title={t('compliance.overview.rights.restriction.title')}
              article="Art. 18"
              description={t('compliance.overview.rights.restriction.description')}
              status="partial"
            />
          </div>
        </div>

        {/* Compliance Checklist - Now dynamic from API */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold mb-4 flex items-center">
            <CheckCircle className="w-5 h-5 mr-2 text-green-600" />
            {t('compliance.overview.checklist.title')}
          </h3>
          <div className="space-y-3">
            <ChecklistItem
              label={t('compliance.overview.checklist.legalBasis')}
              checked={checklist.find(c => c.name === 'legal_basis')?.checked ?? true}
            />
            <ChecklistItem
              label={t('compliance.overview.checklist.privacyNotices')}
              checked={checklist.find(c => c.name === 'privacy_notices')?.checked ?? true}
            />
            <ChecklistItem
              label={t('compliance.overview.checklist.telematicsConsent')}
              checked={checklist.find(c => c.name === 'telematics_consent')?.checked ?? false}
            />
            <ChecklistItem
              label={t('compliance.overview.checklist.privacyMode')}
              checked={checklist.find(c => c.name === 'privacy_mode')?.checked ?? true}
            />
            <ChecklistItem
              label={t('compliance.overview.checklist.retentionPolicy')}
              checked={checklist.find(c => c.name === 'retention_policy')?.checked ?? false}
            />
            <ChecklistItem
              label={t('compliance.overview.checklist.dataAccessProcess')}
              checked={checklist.find(c => c.name === 'data_access_process')?.checked ?? true}
            />
            <ChecklistItem
              label={t('compliance.overview.checklist.auditLog')}
              checked={checklist.find(c => c.name === 'audit_log')?.checked ?? true}
            />
            <ChecklistItem
              label={t('compliance.overview.checklist.worksCouncilConsultation')}
              checked={checklist.find(c => c.name === 'cse_consultation')?.checked ?? false}
            />
            <ChecklistItem
              label={t('compliance.overview.checklist.dpoDesignated')}
              checked={checklist.find(c => c.name === 'dpo_designated')?.checked ?? false}
            />
          </div>
        </div>

        {/* Alerts - Now dynamic based on real data */}
        {(driversWithoutConsent > 0 || stats.records_to_purge > 0 || stats.pending_requests > 0) && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
            <div className="flex items-start">
              <AlertTriangle className="w-5 h-5 text-amber-600 mr-3 mt-0.5" />
              <div>
                <h4 className="font-semibold text-amber-800">{t('compliance.overview.alerts.title')}</h4>
                <ul className="mt-2 text-sm text-amber-700 space-y-1">
                  {driversWithoutConsent > 0 && (
                    <li>- {t('compliance.overview.alerts.missingConsent', { count: driversWithoutConsent })}</li>
                  )}
                  {stats.records_to_purge > 0 && (
                    <li>- {t('compliance.overview.alerts.recordsToPurge', { count: stats.records_to_purge })}</li>
                  )}
                  {stats.pending_requests > 0 && (
                    <li>- {t('compliance.overview.alerts.pendingRequests', { count: stats.pending_requests })}</li>
                  )}
                </ul>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  // Overview (dashboard + navigation only) and the two read-only panels never
  // mutate anything — they must stay fully interactive for an observer profile.
  // The other tabs hold real write forms (toggle privacy mode, approve consent,
  // purge data, save scoring config, publish notices) and stay behind the guard.
  const READ_ONLY_TABS = new Set(['overview', 'country-rules', 'audit']);

  const renderContent = () => {
    switch (activeTab) {
      case 'overview':
        return renderOverview();
      case 'privacy-mode':
        return <PrivacyModePanel onMessage={onMessage} />;
      case 'consent':
        return <ConsentManagement onMessage={onMessage} />;
      case 'data-requests':
        return <DriverDataRequests onMessage={onMessage} />;
      case 'retention':
        return <DataRetentionPanel onMessage={onMessage} />;
      case 'scoring':
        return <PerformanceScoringConfig onMessage={onMessage} />;
      case 'country-rules':
        return <CountryComplianceRulesPanel />;
      case 'audit':
        return <AuditLogViewer onMessage={onMessage} />;
      case 'notices':
        return <PrivacyNoticesPanel onMessage={onMessage} />;
      case 'telematics':
        return (
          <React.Suspense fallback={<div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-blue-600" /></div>}>
            <TelematicsPrivacyPanel onMessage={onMessage} />
          </React.Suspense>
        );
      default:
        return renderOverview();
    }
  };

  return (
    <div className="space-y-4">
      {/* Header Card */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex justify-between items-start mb-6">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <Shield className="w-6 h-6 text-blue-600" />
              {t('compliance.header.title')}
            </h2>
            <p className="text-gray-600 mt-1 flex items-center gap-2 flex-wrap">
              <span>{t('compliance.header.subtitle')}</span>
              <span className="text-gray-300">|</span>
              <span className="text-xs text-gray-500 flex items-center gap-1">
                <Info className="w-3 h-3" />
                {t('compliance.header.regulation')}
              </span>
            </p>
          </div>
        </div>

        {/* Tabs — filtered by V3 permissions */}
        <div className="pt-4 border-t border-gray-200">
          <nav className="flex flex-wrap gap-2">
            {GDPR_TABS.filter(tab => visibleTabs.some(vt => vt.id === tab.key)).map(tab => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`flex items-center px-3 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${
                  activeTab === tab.key
                    ? 'bg-blue-500 text-white'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                <tab.icon className="w-4 h-4 mr-2" />
                {t(tab.labelKey)}
              </button>
            ))}
          </nav>
        </div>
      </div>

      {/* Content — only the tabs holding write forms go behind the guard; Overview,
          country rules and the audit log are pure consultation and must stay usable
          for an observer profile (masking read access would be a regression). */}
      {READ_ONLY_TABS.has(activeTab) ? (
        renderContent()
      ) : (
        <ReadOnlyGuard moduleId="compliance">
          {renderContent()}
        </ReadOnlyGuard>
      )}
    </div>
  );
}

// Helper Components
function ComplianceCard({ icon: Icon, title, value, status, description }) {
  const statusColors = {
    good: 'text-green-600 bg-green-50',
    warning: 'text-amber-600 bg-amber-50',
    danger: 'text-red-600 bg-red-50',
    neutral: 'text-blue-600 bg-blue-50'
  };

  return (
    <div className="bg-white rounded-lg shadow p-4">
      <div className="flex items-center justify-between mb-2">
        <Icon className={`w-6 h-6 ${statusColors[status].split(' ')[0]}`} />
        <span className={`text-2xl font-bold ${statusColors[status].split(' ')[0]}`}>
          {value}
        </span>
      </div>
      <h4 className="font-medium text-gray-900">{title}</h4>
      <p className="text-sm text-gray-500">{description}</p>
    </div>
  );
}

function QuickActionButton({ icon: Icon, label, onClick }) {
  return (
    <button
      onClick={onClick}
      className="flex flex-col items-center p-4 border border-gray-200 rounded-lg hover:bg-blue-50 hover:border-blue-300 transition-colors"
    >
      <Icon className="w-6 h-6 text-blue-600 mb-2" />
      <span className="text-sm text-gray-700">{label}</span>
    </button>
  );
}

function RightCard({ t, title, article, description, status }) {
  // Construit au rendu : une constante de module serait figée avant le changement de langue.
  const statusConfig = {
    implemented: { label: t('compliance.overview.rights.status.implemented'), color: 'bg-green-100 text-green-800' },
    partial: { label: t('compliance.overview.rights.status.partial'), color: 'bg-amber-100 text-amber-800' },
    pending: { label: t('compliance.overview.rights.status.pending'), color: 'bg-gray-100 text-gray-800' }
  };

  return (
    <div className="border border-gray-200 rounded-lg p-4">
      <div className="flex items-start justify-between mb-2">
        <h4 className="font-semibold text-gray-900">{title}</h4>
        <span className="text-xs text-gray-500">{article}</span>
      </div>
      <p className="text-sm text-gray-600 mb-3">{description}</p>
      <span className={`inline-block px-2 py-1 rounded text-xs font-medium ${statusConfig[status].color}`}>
        {statusConfig[status].label}
      </span>
    </div>
  );
}

function ChecklistItem({ label, checked }) {
  return (
    <div className="flex items-center">
      <div className={`w-5 h-5 rounded-full flex items-center justify-center mr-3 ${
        checked ? 'bg-green-100 text-green-600' : 'bg-gray-100 text-gray-400'
      }`}>
        {checked ? (
          <CheckCircle className="w-4 h-4" />
        ) : (
          <div className="w-2 h-2 rounded-full bg-current" />
        )}
      </div>
      <span className={checked ? 'text-gray-700' : 'text-gray-500'}>
        {label}
      </span>
    </div>
  );
}

export default GDPRComplianceView;
